import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Calendar,
  Repeat,
  Link2,
  Users,
  Mail,
  Palette,
  ChevronDown,
  Check,
} from "lucide-react";
import type { AudienceMode } from "@core/types/poll";
import { GroupSelector } from "../groups/GroupSelector";
import type { PollData } from "@core/hooks/usePolls";
import { supabase } from "../../lib/supabase";

interface Template {
  id: string;
  name: string;
  primary_color: string;
}

interface IntentBarMobileProps {
  recurring: boolean;
  onCadenceChange: (recurring: boolean) => void;
  cadenceLocked?: boolean;

  audienceMode: AudienceMode;
  onAudienceModeChange: (mode: AudienceMode) => void;
  emails: string;
  onEmailsChange: (value: string) => void;
  groupId?: string;
  groupName?: string;
  onGroupSelect: (
    emails: string[],
    group?: { id: string; name: string },
  ) => void;
  currentPollData?: PollData;

  templateId: string | null;
  onTemplateChange: (templateId: string | null) => void;
  templateLocked?: boolean;
}

type SheetKey = "cadence" | "audience" | "template" | null;

const emailList = (raw: string): string[] =>
  raw
    .split(/[,;\n]+/)
    .map((e) => e.trim())
    .filter(Boolean);

/**
 * Version compacte de la barre Cadence/Audience/Modèle pour écrans étroits
 * (< md). Remplace les 3 blocs pleine largeur (qui repoussent le calendrier
 * d'environ 370px, cf. handoff "Options en haut pour mobile plus petites")
 * par une rangée de 3 pills à libellés courts + bottom sheet au tap. Même
 * état/handlers que la version desktop (CadenceToggle/AudienceChip/
 * BrandingTemplateChip) — pas de duplication de logique métier, juste un
 * rendu différent.
 */
export function IntentBarMobile({
  recurring,
  onCadenceChange,
  cadenceLocked = false,
  audienceMode,
  onAudienceModeChange,
  emails,
  onEmailsChange,
  groupId,
  groupName,
  onGroupSelect,
  currentPollData,
  templateId,
  onTemplateChange,
  templateLocked = false,
}: IntentBarMobileProps) {
  const { t } = useTranslation(["creator"]);
  const [sheet, setSheet] = useState<SheetKey>(null);
  const [templates, setTemplates] = useState<Template[]>([]);

  useEffect(() => {
    if (templateLocked) return;
    supabase.functions
      .invoke("templates", { method: "GET" })
      .then(({ data }) => setTemplates(Array.isArray(data) ? data : []));
  }, [templateLocked]);

  const audienceOptions: {
    value: AudienceMode;
    icon: React.ReactNode;
    title: string;
    desc: string;
  }[] = [
    {
      value: "link",
      icon: <Link2 className="w-4 h-4 text-green-400" />, // color-exempt: meme categorie audience-type que AudienceChip.tsx (link=vert, groupe=violet, emails=bleu)
      title: t("creator:intent.audience_link_title"),
      desc: t("creator:intent.audience_link_desc"),
    },
    {
      value: "group",
      icon: <Users className="w-4 h-4 text-purple-400" />, // color-exempt
      title: t("creator:intent.audience_group_title"),
      desc: t("creator:intent.audience_group_desc"),
    },
    {
      value: "emails",
      icon: <Mail className="w-4 h-4 text-blue-400" />, // color-exempt
      title: t("creator:intent.audience_email_title"),
      desc: t("creator:intent.audience_email_desc"),
    },
  ];

  const selectedTemplate = templates.find((tpl) => tpl.id === templateId);

  const cadenceChipLabel = recurring
    ? t("creator:intent.cadence_recurring_short")
    : t("creator:intent.cadence_once_short");
  const audienceChipLabel =
    audienceMode === "link"
      ? t("creator:intent.audience_link_short")
      : audienceMode === "group"
        ? t("creator:intent.audience_group_short")
        : t("creator:intent.audience_email_short");
  const templateChipLabel = selectedTemplate
    ? selectedTemplate.name
    : t("creator:intent.template_none_short");

  const pillBase =
    "flex items-center gap-1.5 flex-shrink-0 whitespace-nowrap rounded-full border bg-card px-3.5 py-2.5 text-[12.5px] font-medium text-foreground transition-colors";
  const pillDefault = "border-border";
  const pillAccent =
    "border-primary/40 shadow-[0_0_0_1px_hsl(var(--primary)/0.24)]";

  const closeSheet = () => setSheet(null);

  return (
    <div className="md:hidden" data-testid="intent-bar-mobile">
      <div
        className="flex gap-2 overflow-x-auto pb-1"
        style={{ scrollbarWidth: "none" }}
        data-testid="intent-chip-row"
      >
        <button
          type="button"
          onClick={() => setSheet("cadence")}
          data-testid="intent-chip-cadence"
          className={`${pillBase} ${recurring ? pillAccent : pillDefault}`}
        >
          {recurring ? (
            <Repeat className="w-3.5 h-3.5 text-muted-foreground" />
          ) : (
            <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
          )}
          {cadenceChipLabel}
          <ChevronDown className="w-3 h-3 text-muted-foreground" />
        </button>

        <button
          type="button"
          onClick={() => setSheet("audience")}
          data-testid="intent-chip-audience"
          className={`${pillBase} ${audienceMode === "group" ? pillAccent : pillDefault}`}
        >
          {audienceMode === "link" && (
            <Link2 className="w-3.5 h-3.5 text-green-400" /> // color-exempt
          )}
          {audienceMode === "group" && (
            <Users className="w-3.5 h-3.5 text-purple-400" /> // color-exempt
          )}
          {audienceMode === "emails" && (
            <Mail className="w-3.5 h-3.5 text-blue-400" /> // color-exempt
          )}
          {audienceChipLabel}
          <ChevronDown className="w-3 h-3 text-muted-foreground" />
        </button>

        <button
          type="button"
          disabled={templateLocked}
          onClick={() => !templateLocked && setSheet("template")}
          data-testid="intent-chip-template"
          className={`${pillBase} ${templateLocked ? "opacity-50" : templateId ? pillAccent : pillDefault}`}
        >
          <Palette className="w-3.5 h-3.5 text-primary" />
          {templateChipLabel}
          <ChevronDown className="w-3 h-3 text-muted-foreground" />
        </button>
      </div>

      {sheet && (
        <div className="fixed inset-0 z-[70]" data-testid="intent-sheet-scrim">
          <div
            className="absolute inset-0 bg-black/55"
            onClick={closeSheet}
            data-testid="intent-sheet-backdrop"
          />
          <div
            // pb-[calc(...)] : la tabbar mobile fixe (ResponsiveLayout,
            // DIMENSIONS.mobileNav = 48px) est en z-[60], sous cette sheet
            // (z-[70]) mais sa hauteur doit rester dégagée pour que le
            // dernier bouton (ex "Terminé") ne soit pas collé au bord.
            className="absolute left-0 right-0 bottom-0 rounded-t-2xl border-t border-border bg-popover px-4 pt-2.5 max-h-[80vh] overflow-y-auto"
            style={{ paddingBottom: "calc(48px + 1.5rem)" }}
            data-testid="intent-sheet"
          >
            <div className="mx-auto mb-3.5 h-1 w-9 rounded-full bg-muted-foreground/30" />

            {sheet === "cadence" && (
              <>
                <h4 className="mb-3 text-[15px] font-semibold text-foreground">
                  {t("creator:intent.cap_cadence")}
                </h4>
                <button
                  type="button"
                  onClick={() => {
                    onCadenceChange(false);
                    closeSheet();
                  }}
                  data-testid="intent-sheet-cadence-once"
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                    !recurring ? "bg-muted" : "hover:bg-muted"
                  }`}
                >
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-card">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-foreground">
                      {t("creator:intent.cadence_once")}
                    </span>
                    <span className="block text-[11.5px] text-muted-foreground">
                      {t("creator:intent.cadence_once_sub")}
                    </span>
                  </span>
                  {!recurring && (
                    <Check className="h-4 w-4 flex-shrink-0 text-primary" />
                  )}
                </button>
                <button
                  type="button"
                  disabled={cadenceLocked}
                  onClick={() => {
                    if (cadenceLocked) return;
                    onCadenceChange(true);
                    closeSheet();
                  }}
                  data-testid="intent-sheet-cadence-recurring"
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                    cadenceLocked
                      ? "opacity-50 cursor-not-allowed"
                      : recurring
                        ? "bg-muted"
                        : "hover:bg-muted"
                  }`}
                >
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-card">
                    <Repeat className="w-4 h-4 text-muted-foreground" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-foreground">
                      {t("creator:intent.cadence_recurring")}
                    </span>
                    <span className="block text-[11.5px] text-muted-foreground">
                      {t("creator:intent.cadence_recurring_sub")}
                    </span>
                  </span>
                  {recurring && (
                    <Check className="h-4 w-4 flex-shrink-0 text-primary" />
                  )}
                </button>
              </>
            )}

            {sheet === "audience" && (
              <>
                <h4 className="mb-3 text-[15px] font-semibold text-foreground">
                  {t("creator:intent.cap_audience")}
                </h4>
                {audienceOptions.map((opt) => {
                  const selected = opt.value === audienceMode;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => onAudienceModeChange(opt.value)}
                      data-testid={`intent-sheet-audience-${opt.value}`}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                        selected ? "bg-muted" : "hover:bg-muted"
                      }`}
                    >
                      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-card">
                        {opt.icon}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-foreground">
                          {opt.title}
                        </span>
                        <span className="block text-[11.5px] text-muted-foreground">
                          {opt.desc}
                        </span>
                      </span>
                      {selected && (
                        <Check className="h-4 w-4 flex-shrink-0 text-primary" />
                      )}
                    </button>
                  );
                })}

                {/* Détail contextuel dans la sheet (elle a la place, cf. handoff) —
                    ne pousse pas le calendrier comme un bloc dans le corps principal. */}
                {audienceMode === "group" && (
                  <div className="mt-3">
                    <GroupSelector
                      currentEmails={emailList(emails)}
                      selectedGroupId={groupId}
                      onSelectGroup={onGroupSelect}
                      currentPollData={currentPollData}
                    />
                  </div>
                )}
                {audienceMode === "emails" && (
                  <div className="mt-3">
                    <textarea
                      rows={3}
                      value={emails}
                      onChange={(e) => onEmailsChange(e.target.value)}
                      placeholder="email1@example.com, email2@example.com"
                      data-testid="intent-sheet-emails-input"
                      className="w-full rounded-md border border-border bg-card px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                )}
                <button
                  type="button"
                  onClick={closeSheet}
                  data-testid="intent-sheet-done"
                  className="mt-4 w-full rounded-lg bg-muted py-2.5 text-sm font-semibold text-foreground"
                >
                  {t("creator:intent.sheet_done")}
                </button>
              </>
            )}

            {sheet === "template" && (
              <>
                <h4 className="mb-3 text-[15px] font-semibold text-foreground">
                  {t("creator:intent.cap_template")}
                </h4>
                <button
                  type="button"
                  onClick={() => {
                    onTemplateChange(null);
                    closeSheet();
                  }}
                  data-testid="intent-sheet-template-none"
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                    templateId === null ? "bg-muted" : "hover:bg-muted"
                  }`}
                >
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-card">
                    <Palette className="w-4 h-4 text-muted-foreground" />
                  </span>
                  <span className="min-w-0 flex-1 text-sm font-semibold text-foreground">
                    {t("creator:intent.template_none_title")}
                  </span>
                  {templateId === null && (
                    <Check className="h-4 w-4 flex-shrink-0 text-primary" />
                  )}
                </button>
                {templates.length === 0 ? (
                  <p className="px-3 py-2 text-[11.5px] text-muted-foreground">
                    {t("creator:intent.template_empty")}
                  </p>
                ) : (
                  templates.map((tpl) => {
                    const isSelected = tpl.id === templateId;
                    return (
                      <button
                        key={tpl.id}
                        type="button"
                        onClick={() => {
                          onTemplateChange(tpl.id);
                          closeSheet();
                        }}
                        data-testid={`intent-sheet-template-${tpl.id}`}
                        className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                          isSelected ? "bg-muted" : "hover:bg-muted"
                        }`}
                      >
                        <span
                          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-border"
                          style={{ backgroundColor: tpl.primary_color }}
                        />
                        <span className="min-w-0 flex-1 text-sm font-semibold text-foreground">
                          {tpl.name}
                        </span>
                        {isSelected && (
                          <Check className="h-4 w-4 flex-shrink-0 text-primary" />
                        )}
                      </button>
                    );
                  })
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default IntentBarMobile;
